import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectExtraMembersInfo, selectIsExtraMembersFeatureAvailable, } from 'reducers/selectors/sharedPlans';
import { useMainStackNavigation, } from 'utils/types/navigationV6';
import { selectIsUserUltimate } from 'features/premium/selectors/tier';
import useRequiredTierCallback from 'features/premium/hooks/useRequiredTierCallback';
import { Tier } from 'features/premium/entities';
import { clearSuggestFrom, setSuggestFrom } from 'actions/sharedPlans';
import { showAddExtraMembersModal, } from '../components/AddExtraMembersModal';
export function getRouteKey(navigation) {
    const state = navigation.getState();
    const currentRoute = state.routes[state.index];
    return currentRoute.key;
}
export function useStoreExtraMembersSuggestion() {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    return useCallback(({ suggestGroupId, suggestSpaceId }) => {
        if (suggestSpaceId) {
            dispatch(setSuggestFrom({
                type: 'space',
                id: suggestSpaceId,
                routeKey: getRouteKey(navigation),
            }));
        }
        else if (suggestGroupId) {
            dispatch(setSuggestFrom({
                type: 'group',
                id: suggestGroupId,
                routeKey: getRouteKey(navigation),
            }));
        }
        else {
            dispatch(clearSuggestFrom());
        }
    }, []);
}
/**
 * @returns If the extra-member feature is available, return a function that shows
 *   a modal (if appropriate) that encourages purchasing/using.
 *   Otherwise, return null.
 */
export function useTryAdvertiseExtraMembers() {
    const navigation = useMainStackNavigation();
    const extraMembersInfo = useAppSelector(selectExtraMembersInfo);
    const isExtraMembersFeatureAvailable = useAppSelector(selectIsExtraMembersFeatureAvailable);
    const isUserUltimate = useAppSelector(selectIsUserUltimate);
    const goToIntroNeedUpgrade = useRequiredTierCallback((numberOfSlotsNeeded) => navigation.navigate('ExtraMembersIntro', { numberOfSlotsNeeded }), Tier.ultimate);
    const storeSuggestion = useStoreExtraMembersSuggestion();
    const tryAdvertiseExtraMembers = useCallback(({ potentialExtraMembers, wasAddingToDisplayName, suggestGroupId, suggestSpaceId, }) => {
        const contacts = potentialExtraMembers.map((contact) => {
            let isExtraMember = false;
            if (extraMembersInfo && contact.handle) {
                extraMembersInfo.seats.forEach((seat) => {
                    if (isExtraMember === false) {
                        if (seat.member?.handle === contact.handle) {
                            isExtraMember = true;
                        }
                        else if (seat.invite?.handle === contact.handle) {
                            isExtraMember = 'invited';
                        }
                    }
                });
            }
            return { contact, isExtraMember };
        });
        // not a good occasion
        if (contacts.every(({ contact, isExtraMember }) => !!isExtraMember || contact.hasSubscription)) {
            return;
        }
        if (extraMembersInfo &&
            extraMembersInfo.slotsLeft + extraMembersInfo.freeSeats === 0) {
            return;
        }
        const newSlotsNeeded = contacts.filter(({ isExtraMember }) => !isExtraMember).length -
            (extraMembersInfo?.freeSeats ?? 0);
        if (newSlotsNeeded <= 0) {
            // show the advertisement, and take the user to extra-member management screen
            showAddExtraMembersModal({
                onConfirm: () => {
                    storeSuggestion({ suggestSpaceId, suggestGroupId });
                    navigation.navigate('ManageExtraMembers');
                },
                wasAddingToDisplayName,
                contacts,
                needUpgrade: !isUserUltimate,
            });
            return;
        }
        // show the advertisement, and take the user to purchase slots
        let goToExtraMembers;
        if (isUserUltimate) {
            goToExtraMembers =
                !extraMembersInfo || extraMembersInfo.totalSeats === 0
                    ? () => navigation.navigate('ExtraMembersIntro', {
                        numberOfSlotsNeeded: newSlotsNeeded,
                    })
                    : () => navigation.navigate('SelectNumberOfSlots', {
                        initialNumber: newSlotsNeeded,
                    });
        }
        else {
            goToExtraMembers = () => {
                goToIntroNeedUpgrade(newSlotsNeeded);
            };
        }
        showAddExtraMembersModal({
            onConfirm: () => {
                storeSuggestion({ suggestSpaceId, suggestGroupId });
                goToExtraMembers();
            },
            wasAddingToDisplayName,
            contacts,
            needUpgrade: !isUserUltimate,
        });
    }, [extraMembersInfo, goToIntroNeedUpgrade, isUserUltimate, storeSuggestion]);
    return isExtraMembersFeatureAvailable ? tryAdvertiseExtraMembers : null;
}
